import { z } from "zod"
import {
  contactTypeEnum,
  estateRoleEnum,
  insuranceRoleEnum,
  maritalStatusEnum,
  relationshipTypeEnum,
  rolePositionEnum,
} from "../../../db/schema/contact"
import { zLegacyBeneficiaryType, zParentRelationship } from "../../../services/contact/types"

export const contactTypeSchema = z.enum(contactTypeEnum.enumValues, {
  errorMap: () => ({ message: "Please select a valid contact type" }),
})
export type ContactType = z.infer<typeof contactTypeSchema>

export const estateRoleSchema = z.enum(estateRoleEnum.enumValues, {
  errorMap: () => ({ message: "Please select a valid estate role" }),
})
export type EstateRole = z.infer<typeof estateRoleSchema>

export const rolePositionSchema = z.enum(rolePositionEnum.enumValues, {
  errorMap: () => ({ message: "Please select a valid role position" }),
})
export type RolePosition = z.infer<typeof rolePositionSchema>

export const maritalStatusSchema = z.enum(maritalStatusEnum.enumValues, {
  errorMap: () => ({ message: "Please select a valid marital status" }),
})
export type MaritalStatus = z.infer<typeof maritalStatusSchema>

export const insuranceRoleSchema = z.enum(insuranceRoleEnum.enumValues, {
  errorMap: () => ({ message: "Please select a valid insurance role" }),
})
export type InsuranceRole = z.infer<typeof insuranceRoleSchema>

/** @deprecated Remove after frontend migrates to beneficiary tRPC procedures (#689) */
export const beneficiaryTypeSchema = zLegacyBeneficiaryType
/** @deprecated Remove after frontend migrates to beneficiary tRPC procedures (#689) */
export type BeneficiaryType = z.infer<typeof beneficiaryTypeSchema>

export const relationshipTypeSchema = z.enum(relationshipTypeEnum.enumValues, {
  errorMap: () => ({ message: "Please select a valid relationship type" }),
})
export type RelationshipType = z.infer<typeof relationshipTypeSchema>

export const zAddress = z.object({
  addressLine1: z.string(),
  addressLine2: z.string().nullish(),
  city: z.string(),
  stateProvinceId: z.string().uuid().nullish(),
  county: z.string().nullish(),
  zipPostalCode: z.string(),
  country: z.string(),
})

export const zEstateContactRoleInput = z.object({
  estateRole: estateRoleSchema,
  rolePosition: rolePositionSchema,
  profilePictureUrl: z.string().url().nullish(),
  estateInstructions: z.string().nullish(),
  startDate: z.string().nullish(),
  endDate: z.string().nullish(),
  notes: z.string().nullish(),
})

export type EstateContactRoleInput = z.infer<typeof zEstateContactRoleInput>

export const zAddEstateContactInput = z
  .object({
    estateId: z.string().uuid(),
    estateRoles: z.array(zEstateContactRoleInput).optional(),

    // contact fields
    contactType: contactTypeSchema.nullish(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().email().optional(),
    phoneNumber: z.string().nullish(),
    dateOfBirth: z.string().nullish(),
    dateOfDeath: z.string().nullish(),
    locationId: z.string().uuid().nullish(),
    address: zAddress.optional(),
    maritalStatus: maritalStatusSchema.nullish(),
    maritalStatusDate: z.string().nullish(),
    relationshipStartDate: z.string().nullish(),
    relationshipEndDate: z.string().nullish(),
    /** @deprecated Remove after frontend migrates to beneficiary tRPC procedures (#689) */
    beneficiaryType: beneficiaryTypeSchema.nullish(),
    /** @deprecated Remove after frontend migrates to beneficiary tRPC procedures (#689) */
    beneficiaryTypes: z.array(beneficiaryTypeSchema).nullish(),
    /** @deprecated Remove after frontend migrates to beneficiary tRPC procedures (#689) */
    residualPercentage: z.number().int().optional(),
    insuranceRole: insuranceRoleSchema.nullish(),
    parentContactId: z.string().uuid().nullish(),
    relationshipType: relationshipTypeSchema.nullish(),

    assetId: z.string().uuid().optional(),
    assetOwnershipPercentage: z.number().int().optional(),
    isDraft: z.boolean().optional(),
  })
  .refine(
    (data) => {
      // Cannot provide both locationId and address
      return !(data.locationId && data.address)
    },
    {
      message: "Cannot provide both locationId and address. Please provide only one.",
      path: ["locationId", "address"],
    },
  )
  .refine(
    (data) => {
      // parentContactId and relationshipType must both be present or both absent
      const hasParent = !!data.parentContactId
      const hasType = !!data.relationshipType
      return hasParent === hasType
    },
    {
      message: "parentContactId and relationshipType must both be provided together.",
      path: ["parentContactId", "relationshipType"],
    },
  )

export type AddEstateContactInput = z.infer<typeof zAddEstateContactInput>
export const zAddEstateContactOutput = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  contactType: contactTypeSchema.nullable(),
})
export type AddEstateContactOutput = z.infer<typeof zAddEstateContactOutput>

export const UpdateEstateContactInput = z
  .object({
    contactId: z.string().uuid(),

    // contact fields
    contactType: contactTypeSchema.nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    email: z.string().email().nullish(),
    phoneNumber: z.string().nullish(),
    dateOfBirth: z.string().nullish(),
    dateOfDeath: z.string().nullish(),
    locationId: z.string().uuid().nullish(),
    address: zAddress.optional(),
    maritalStatus: maritalStatusSchema.nullish(),
    maritalStatusDate: z.string().nullish(),
    relationshipStartDate: z.string().nullish(),
    relationshipEndDate: z.string().nullish(),
    /** @deprecated Remove after frontend migrates to beneficiary tRPC procedures (#689) */
    beneficiaryType: beneficiaryTypeSchema.nullish(),
    /** @deprecated Remove after frontend migrates to beneficiary tRPC procedures (#689) */
    beneficiaryTypes: z.array(beneficiaryTypeSchema).nullish(),
    /** @deprecated Remove after frontend migrates to beneficiary tRPC procedures (#689) */
    residualPercentage: z.number().int().nullish(),
    insuranceRole: insuranceRoleSchema.nullish(),
  })
  .refine(
    (data) => {
      // Cannot provide both locationId and address
      return !(data.locationId && data.address)
    },
    {
      message: "Cannot provide both locationId and address. Please provide only one.",
      path: ["locationId", "address"],
    },
  )

export type UpdateEstateContactInput = z.infer<typeof UpdateEstateContactInput>

export const ListLocationsInput = z.object({
  estateId: z.string().uuid(),
})

export type ListLocationsInput = z.infer<typeof ListLocationsInput>

export const zListLocationsOutput = z.array(
  z.object({
    id: z.string().uuid(),
    addressLine1: z.string(),
    addressLine2: z.string().nullish(),
    city: z.string(),
    stateProvinceId: z.string().uuid().nullish(),
    county: z.string().nullish(),
    zipPostalCode: z.string().nullish(),
    country: z.string().nullish(),
  }),
)

export type ListLocationsOutput = z.infer<typeof zListLocationsOutput>

export const ListStateProvincesInput = z.object({
  countryCode: z.string().length(2),
})

export type ListStateProvincesInput = z.infer<typeof ListStateProvincesInput>

export const UpdateLocationInput = z.object({
  locationId: z.string().uuid(),
  addressLine1: z.string().optional(),
  addressLine2: z.string().nullish(),
  city: z.string().optional(),
  stateProvinceId: z.string().uuid().nullish(),
  county: z.string().nullish(),
  zipPostalCode: z.string().optional(),
  country: z.string().optional(),
})

export type UpdateLocationInput = z.infer<typeof UpdateLocationInput>

export const zUpdateEstateContactRolePositionInput = z.object({
  estateContactRole: z.string().uuid(),
  rolePosition: z.enum(rolePositionEnum.enumValues),
})

export type UpdateEstateContactRolePositionInput = z.infer<
  typeof zUpdateEstateContactRolePositionInput
>

export const zDeleteContactInput = z.object({
  contactId: z.string().uuid(),
  estateId: z.string().uuid(),
})

export type DeleteContactInput = z.infer<typeof zDeleteContactInput>

export const zAddEstateContactRoleInput = z.object({
  estateId: z.string().uuid(),
  contactId: z.string().uuid(),
  estateRole: estateRoleSchema,
  rolePosition: rolePositionSchema,
  profilePictureUrl: z.string().url().nullish(),
  estateInstructions: z.string().nullish(),
  startDate: z.string().nullish(),
  endDate: z.string().nullish(),
  notes: z.string().nullish(),
  isDraft: z.boolean().optional(),
})

export type AddEstateContactRoleInput = z.infer<typeof zAddEstateContactRoleInput>

export const zRemoveEstateContactRoleInput = z.object({
  estateContactRoleId: z.string().uuid(),
})

export type RemoveEstateContactRoleInput = z.infer<typeof zRemoveEstateContactRoleInput>

export const zUpdateEstateContactRoleInput = z
  .object({
    estateContactRoleId: z.string().uuid(),
    notes: z.string().nullish(),
    estateInstructions: z.string().nullish(),
  })
  .refine((data) => data.notes !== undefined || data.estateInstructions !== undefined, {
    message: "At least one field (notes or estateInstructions) must be provided.",
  })

export type UpdateEstateContactRoleInput = z.infer<typeof zUpdateEstateContactRoleInput>

// Contact Relationships

export const zAddContactRelationshipInput = z.object({
  estateId: z.string().uuid(),
  parentContactId: z.string().uuid(),
  childContactId: z.string().uuid(),
  relationshipType: relationshipTypeSchema,
})
export type AddContactRelationshipInput = z.infer<typeof zAddContactRelationshipInput>

export const zAddContactRelationshipOutput = z.object({
  id: z.string().uuid(),
})
export type AddContactRelationshipOutput = z.infer<typeof zAddContactRelationshipOutput>

export const zRemoveContactRelationshipInput = z.object({
  relationshipId: z.string().uuid(),
  estateId: z.string().uuid(),
})
export type RemoveContactRelationshipInput = z.infer<typeof zRemoveContactRelationshipInput>

export const zRemoveContactRelationshipOutput = z.object({
  success: z.boolean(),
})
export type RemoveContactRelationshipOutput = z.infer<typeof zRemoveContactRelationshipOutput>

export const zGetChildrenByParentInput = z.object({
  parentContactId: z.string().uuid(),
  estateId: z.string().uuid(),
})
export type GetChildrenByParentInput = z.infer<typeof zGetChildrenByParentInput>

const zRelationshipContactOutput = z.object({
  relationshipId: z.string().uuid(),
  id: z.string().uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  contactType: contactTypeSchema.nullable(),
  relationshipType: relationshipTypeSchema,
})

export const zGetChildrenByParentOutput = z.array(zRelationshipContactOutput)
export type GetChildrenByParentOutput = z.infer<typeof zGetChildrenByParentOutput>

export const zGetParentsByChildInput = z.object({
  childContactId: z.string().uuid(),
  estateId: z.string().uuid(),
})
export type GetParentsByChildInput = z.infer<typeof zGetParentsByChildInput>

export const zGetParentsByChildOutput = z.array(zRelationshipContactOutput)
export type GetParentsByChildOutput = z.infer<typeof zGetParentsByChildOutput>

export const zGetIndependentChildrenInput = z.object({
  estateId: z.string().uuid(),
})
export type GetIndependentChildrenInput = z.infer<typeof zGetIndependentChildrenInput>

const zIndependentChildOutput = z.object({
  id: z.string().uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  contactType: contactTypeSchema.nullable(),
})

export const zGetIndependentChildrenOutput = z.array(zIndependentChildOutput)
export type GetIndependentChildrenOutput = z.infer<typeof zGetIndependentChildrenOutput>

export const zChangeParentInput = z.object({
  estateId: z.string().uuid(),
  childContactId: z.string().uuid(),
  newParentContactId: z.string().uuid(),
  relationshipType: relationshipTypeSchema,
})
export type ChangeParentInput = z.infer<typeof zChangeParentInput>

export const zChangeParentOutput = z.object({
  id: z.string().uuid(),
})
export type ChangeParentOutput = z.infer<typeof zChangeParentOutput>

export const zSetChildParentsInput = z.object({
  estateId: z.string().uuid(),
  childContactId: z.string().uuid(),
  parents: z.array(
    z.object({
      parentContactId: z.string().uuid(),
      relationshipType: relationshipTypeSchema,
    }),
  ),
})
export type SetChildParentsInput = z.infer<typeof zSetChildParentsInput>

export const zSetChildParentsOutput = z.object({
  success: z.boolean(),
  parents: z.array(zParentRelationship),
})
export type SetChildParentsOutput = z.infer<typeof zSetChildParentsOutput>
